/**
 * 模型与价目区块（S2）的文案。命名空间 `visualGameAdmin.llm.models.*`。
 *
 * **金额一律是人民币**：接口使用整数 micros，编辑界面使用元，摘要显示元。
 *
 * `verify.reasons.*` 的七个 key 是网关归一后的 `LLMError.reason` 全集
 * （`packages/llm/src/map-error.js`）。后端还会带回一条更长的 `message`（含下一步怎么查）——
 * 它是**错误原文**，进 toast 和单元格的 `title`，不进版面。
 *
 * `en` 与 `zh` 的 key 集必须逐字相同 —— `test/i18n.test.js` 拍平后逐项比对六个语种。
 */

export const en = {
  title: 'Models & pricing',
  add: 'Add model',
  table: {
    model: 'Model',
    enabled: 'Status',
    verify: 'Last test',
    actions: 'Actions',
  },
  empty: {
    title: 'No model yet',
  },
  actions: {
    edit: 'Edit',
    enable: 'Enable',
    disable: 'Disable',
    delete: 'Delete',
    verify: 'Test',
  },
  confirm: {
    disable: 'Disable this model?',
    delete: 'Delete this model?',
  },
  form: {
    createTitle: 'Add a model',
    editTitle: 'Edit model',
    model: 'Model name',
    modelLocked: 'Identity, not editable',
    params: 'Model params (JSON)',
    enabled: 'Status',
    submit: 'Save model',
  },
  pricing: {
    mode: 'Pricing mode',
    unit: 'CNY / 1M tokens',
    modes: {
      token_tiered: 'Token tiers',
    },
    promptPerM: 'Prompt / 1M tokens (mil)',
    completionPerM: 'Completion / 1M tokens (mil)',
    cachedPerM: 'Cached / 1M tokens (mil)',
    cachedFallback: 'Same as prompt',
    perCall: 'Price per call (mil)',
    tiers: {
      title: 'Token price tiers',
      upTo: 'Input tokens up to',
      inputRate: 'Input (CNY / 1M)',
      outputRate: 'Output (CNY / 1M)',
      cachedRate: 'Cached input (CNY / 1M)',
      cacheWriteRate: 'Cache write (CNY / 1M)',
      unbounded: 'unbounded',
      add: 'Add tier',
      remove: 'Remove',
      tier: 'Tier {index}',
    },
  },
  verify: {
    running: 'Testing…',
    never: 'Not tested',
    reasons: {
      auth: 'Authentication failed',
      rateLimit: 'Rate limited',
      transient: 'Cannot reach upstream',
      timeout: 'Upstream timed out',
      badRequest: 'Request rejected',
      aborted: 'Request aborted',
      unknown: 'Call failed',
    },
  },
  errors: {
    required: 'Required.',
    tooLong: 'Too long.',
    nonNegInt: 'Must be an integer of 0 or more.',
    nonNegPrice: 'Must be 0 or more, with up to 6 decimal places.',
    positiveInt: 'Must be an integer of 1 or more.',
    invalidJson: 'Not valid JSON.',
    notObject: 'Must be a JSON object.',
    tiers: {
      lastTierUnbounded: 'The last tier must be unbounded: leave its "up to" empty.',
      unboundedOnlyLast: 'Only the last tier may be unbounded.',
      strictlyIncreasing: 'The numeric upper bounds must strictly increase.',
      noTiers: 'At least one tier is required.',
    },
  },
  toast: {
    saved: 'Model saved.',
    deleted: 'Model deleted.',
    failed: 'The write was rejected.',
    verifyOk: 'This model really answers.',
  },
}

export const zh = {
  title: '模型与价目',
  add: '新增模型',
  table: {
    model: '模型',
    enabled: '状态',
    verify: '最近测试',
    actions: '操作',
  },
  empty: {
    title: '还没有模型',
  },
  actions: {
    edit: '编辑',
    enable: '启用',
    disable: '停用',
    delete: '删除',
    verify: '测试',
  },
  confirm: {
    disable: '停用这个模型？',
    delete: '删除这个模型？',
  },
  form: {
    createTitle: '新增模型',
    editTitle: '编辑模型',
    model: '模型名',
    modelLocked: '身份，不可改',
    params: '模型级参数（JSON）',
    enabled: '状态',
    submit: '保存模型',
  },
  pricing: {
    mode: '计价模式',
    unit: '人民币元 / 百万 tokens',
    modes: {
      token_tiered: 'Token 分档',
    },
    promptPerM: '输入 / 百万 token（厘）',
    completionPerM: '输出 / 百万 token（厘）',
    cachedPerM: '缓存 / 百万 token（厘）',
    cachedFallback: '同输入价',
    perCall: '每次调用（厘）',
    tiers: {
      title: 'Token 价格分档',
      upTo: '输入 token 上限',
      inputRate: '输入（元 / 百万）',
      outputRate: '输出（元 / 百万）',
      cachedRate: '缓存输入（元 / 百万）',
      cacheWriteRate: '缓存写入（元 / 百万）',
      unbounded: '不封顶',
      add: '加一档',
      remove: '删掉',
      tier: '第 {index} 档',
    },
  },
  verify: {
    running: '测试中…',
    never: '未测试',
    reasons: {
      auth: '认证失败',
      rateLimit: '被上游限流',
      transient: '连不上上游',
      timeout: '上游超时',
      badRequest: '上游拒绝了请求',
      aborted: '请求被中断',
      unknown: '调用失败',
    },
  },
  errors: {
    required: '必填。',
    tooLong: '太长了。',
    nonNegInt: '必须是 0 或更大的整数。',
    nonNegPrice: '必须是大于等于 0、最多 6 位小数的价格。',
    positiveInt: '必须是 1 或更大的整数。',
    invalidJson: '不是合法的 JSON。',
    notObject: '必须是一个 JSON 对象。',
    tiers: {
      lastTierUnbounded: '末档必须不封顶：把它的「上限」留空。',
      unboundedOnlyLast: '只有末档能不封顶。',
      strictlyIncreasing: '数字上限必须严格递增。',
      noTiers: '至少要有一档。',
    },
  },
  toast: {
    saved: '模型已保存。',
    deleted: '模型已删除。',
    failed: '写入被拒绝。',
    verifyOk: '这个模型真的答得上来。',
  },
}
